import React, { useMemo } from 'react';
import type { ColumnsType } from 'antd/es/table';
import {
  AppCard,
  AppCol,
  AppPagination,
  AppRow,
  AppSpin,
  AppTable,
  AppTableActionButton,
  AppTooltip,
} from '@/components/atoms';
import { convGtJDate } from '@/utils/dateConverter';
import type { InsurerAttention, PaginationInfo } from '../types';

interface Props {
  attentions: InsurerAttention[];
  pagination: PaginationInfo | null;
  searching: boolean;
  selectedAttentionId?: number;
  onDetailClick: (attention: InsurerAttention) => void;
  onPageChange: (page: number) => void;
}

const renderCellWithTooltip = (text: string | null | undefined) => {
  if (!text) return <span className="text-gray-400">-</span>;

  return (
    <AppTooltip title={text} showOnlyOnOverflow>
      <div className="break-words line-clamp-2" dangerouslySetInnerHTML={{ __html: text }} />
    </AppTooltip>
  );
};

const renderPublishDate = (text: string | null | undefined) => {
  if (!text) return '';

  const jDate = convGtJDate(text);
  if (!jDate) return '';

  const yearIdx = jDate.indexOf('年');
  if (yearIdx < 0) return jDate;

  return (
    <>
      <span>{jDate.slice(0, yearIdx + 1)}</span>
      <br />
      <span>{jDate.slice(yearIdx + 1)}</span>
    </>
  );
};

const attachmentColumns = Array.from({ length: 5 }, (_, index) => {
  const key = `attachment${index + 1}`;

  return {
    title: `資料${index + 1}`,
    dataIndex: key,
    key,
    width: 120,
    render: (text: string) => renderCellWithTooltip(text),
  };
});

export const InsurerAttentionResults: React.FC<Props> = ({
  attentions,
  pagination,
  searching,
  selectedAttentionId,
  onDetailClick,
  onPageChange,
}) => {
  const columns = useMemo<ColumnsType<InsurerAttention>>(
    () => [
      {
        title: '掲載日',
        dataIndex: 'publish_date',
        key: 'publish_date',
        width: 60,
        render: (text: string) => <div className="!w-[60px]">{renderPublishDate(text)}</div>,
      },
      {
        title: '県No',
        dataIndex: 'prefecture_id',
        key: 'prefecture_id',
        width: 80,
        render: (text: number | string | null | undefined) => {
          if (text === null || text === undefined || text === '') return '';
          return String(text).padStart(2, '0');
        },
      },
      {
        title: '都道府県',
        dataIndex: ['prefecture', 'name'],
        key: 'prefecture',
        width: 100,
      },
      {
        title: '保険者名称',
        dataIndex: 'insurer_name',
        key: 'insurer_name',
        width: 150,
      },
      {
        title: '保険者No',
        dataIndex: 'insurer_number',
        key: 'insurer_number',
        width: 100,
      },
      {
        title: '注意事項',
        dataIndex: 'remarks',
        key: 'remarks',
        width: 200,
        render: (text: string) => renderCellWithTooltip(text),
      },
      ...attachmentColumns,
      {
        title: '詳細',
        key: 'action',
        width: 90,
        align: 'center',
        render: (_: unknown, record: InsurerAttention) => (
          <AppTableActionButton
            action="detail"
            onClick={e => {
              e.stopPropagation();
              onDetailClick(record);
            }}
          >
            詳細
          </AppTableActionButton>
        ),
      },
    ],
    [onDetailClick]
  );

  return (
    <AppCard className="relative">
      {searching && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-50 rounded-lg">
          <AppSpin size="large" />
        </div>
      )}

      <AppTable
        columns={columns}
        dataSource={attentions.map(attention => ({
          ...attention,
          key: attention.id,
        }))}
        recordCount={pagination?.total ?? attentions.length}
        pagination={false}
        onRow={record => ({
          className: selectedAttentionId === record.id ? 'bg-blue-100' : '',
        })}
        scroll={{ x: true }}
      />

      {pagination && (
        <AppRow className="mt-6">
          <AppCol xs={24} className="flex justify-center">
            <AppPagination
              current={pagination.current_page}
              total={pagination.total}
              pageSize={pagination.per_page}
              onChange={onPageChange}
              showLessItems
            />
          </AppCol>
        </AppRow>
      )}
    </AppCard>
  );
};
